import type { DataItem, DataItemId } from '@pn/core/domain/data';
import {
  createLayerPatternMetadata,
  type FilterProperty,
  type Layer,
  type LayerPattern,
  type LayerStyle,
} from '@pn/core/domain/layer';
import {
  areFiltersApplied,
  type Filter,
  type LinkOperator,
  type Query,
  type Sort,
} from '@pn/core/domain/query';
import {
  disableBubbleMapping as _disableBubbleMapping,
  enableBubbleMapping as _enableBubbleMapping,
  createSourceItemMapConfig,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { isSelectionItemId } from '@pn/core/operations/mapInteractions/mapSelectionProcessor';
import { smartCombineItems } from '@pn/core/storage/workspace/utils';
import { assert } from '@pn/core/utils/assert';
import { findOrThrow } from '@pn/core/utils/logic';
import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'fast-deep-equal';
import { isArray, isNil, uniq } from 'lodash-es';
import { moduleItemIds, symbolItemIds } from './pnWorkspaceItems';
import type { State } from './workspaceSlice';

export const add: CaseReducer<
  State,
  PayloadAction<{
    itemOrItems: WorkspaceItem | WorkspaceItem[];
    upsert: boolean;
  }>
> = (state, { payload }) => {
  const items = isArray(payload.itemOrItems)
    ? payload.itemOrItems
    : [payload.itemOrItems];

  const incomingItems = Object.fromEntries(items.map((i) => [i.id, i]));

  state.allWorkspaceItems = smartCombineItems(
    state.allWorkspaceItems,
    incomingItems,
    payload.upsert
  );
};

export const create: CaseReducer<State, PayloadAction<WorkspaceItem>> = (
  state,
  { payload }
) => {
  state.allWorkspaceItems[payload.id] = payload;
};

export const update: CaseReducer<State, PayloadAction<WorkspaceItem>> = (
  state,
  { payload }
) => {
  const item = state.allWorkspaceItems[payload.id];
  Object.assign(item, payload, {
    updatedAt: new Date().toISOString(),
  });
};

/**
 * Almost identical to update but does not trigger the sync storage routine.
 */
export const overwrite: CaseReducer<State, PayloadAction<WorkspaceItem>> = (
  state,
  { payload }
) => {
  /* Can be undefined if the item was deleted in the same closure */
  const item = state.allWorkspaceItems[payload.id];
  if (!isNil(item)) Object.assign(item, payload);
};

export const remove: CaseReducer<State, PayloadAction<WorkspaceItem>> = (
  state,
  { payload }
) => {
  const itemIdsToRemove = [payload.id];

  if (isNil(payload.sourceItem)) {
    const childrenItems = Object.values(state.allWorkspaceItems).filter(
      ({ sourceItem }) => sourceItem?.id === payload.id
    );
    itemIdsToRemove.push(...childrenItems.map(({ id }) => id));
  }

  state.idsInWorkspace = state.idsInWorkspace.filter(
    (id) => !itemIdsToRemove.includes(id)
  );
  Object.values(state.allWorkspaceItems).forEach((item) => {
    if (itemIdsToRemove.includes(item.id)) {
      delete state.allWorkspaceItems[item.id];
    }
  });
};

export const removeStackOriginatedItems: CaseReducer<State> = (state) => {
  Object.values(state.allWorkspaceItems).forEach((item) => {
    if (!isNil(item.metadata?.dataOriginDescription)) {
      delete state.allWorkspaceItems[item.id];
    }
  });
  state.idsInWorkspace = state.idsInWorkspace.filter(
    (id) => !isNil(state.allWorkspaceItems[id])
  );
};

export const removeAreasFromWorkspace: CaseReducer<State> = (state) => {
  state.idsInWorkspace = state.idsInWorkspace.filter(
    (id) => !state.allWorkspaceItems[id].metadata?.isQueryArea
  );
};

export const share: CaseReducer<State, PayloadAction<WorkspaceItem>> = (
  state,
  { payload }
) => {
  state.allWorkspaceItems[payload.id].isShared = true;
};

export const unshare: CaseReducer<State, PayloadAction<WorkspaceItem>> = (
  state,
  { payload }
) => {
  state.allWorkspaceItems[payload.id].isShared = false;
};

export const updateInvalidDataItemIds: CaseReducer<
  State,
  PayloadAction<DataItem['_id'][]>
> = (state, { payload }) => {
  state.invalidDataItemIds = payload;
};

export const updateIsFetching: CaseReducer<State, PayloadAction<boolean>> = (
  state,
  { payload }
) => {
  state.isFetching = payload;
};

export const updateIsFetchingStack: CaseReducer<
  State,
  PayloadAction<boolean>
> = (state, { payload }) => {
  state.isFetchingStack = payload;
};

export const updateIsStackAccessDenied: CaseReducer<
  State,
  PayloadAction<boolean>
> = (state, { payload }) => {
  state.isStackAccessDenied = payload;
};

export const updateQueryProperty: CaseReducer<
  State,
  PayloadAction<{ itemId: string; query: Partial<Query> }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.query = { ...item.query, ...payload.query };
};

export const updateFilterPropertyOptions: CaseReducer<
  State,
  PayloadAction<{
    itemId: string;
    filterPropertyOptions: FilterProperty['options'];
  }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  assert(
    item.map.filterProperty,
    'Cannot update filter property options of an item with no filter property'
  );
  item.map.filterProperty.options = payload.filterPropertyOptions;
};

export const addLayer: CaseReducer<
  State,
  PayloadAction<{ itemId: string; layer: Layer }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.map.layers.push(payload.layer);
  item.isProcessed = false;
};

export const removeLayer: CaseReducer<
  State,
  PayloadAction<{ itemId: string; layerId: string }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.map.layers = item.map.layers.filter(({ id }) => id !== payload.layerId);
  item.isProcessed = false;
};

export const updateLayerStyle: CaseReducer<
  State,
  PayloadAction<{ itemId: string; layerId: string; style: LayerStyle }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  const layer = item.map.layers.find(({ id }) => id === payload.layerId);
  if (!isNil(layer)) {
    Object.assign(layer.style, payload.style);
    item.isProcessed = false;
  }
};

export const updateAllLayerStyles: CaseReducer<
  State,
  PayloadAction<{ itemId: string; style: LayerStyle }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.map = {
    ...createSourceItemMapConfig({
      mode: 'duplicate',
      sourceItem: item,
      newLayerStyle: payload.style,
    }),
    originalLayers: item.map.originalLayers,
  };
  item.isProcessed = false;
};

export const updateLayerPattern: CaseReducer<
  State,
  PayloadAction<{
    itemId: string;
    layerId: string;
    pattern: LayerPattern | undefined;
  }>
> = (state, { payload: { itemId, layerId, pattern } }) => {
  const item = state.allWorkspaceItems[itemId];
  const layer = findOrThrow(item.map.layers, ({ id }) => id === layerId);
  layer.metadata = !isNil(pattern)
    ? createLayerPatternMetadata(pattern)
    : undefined;
  item.isProcessed = false;
};

export const overrideLayers: CaseReducer<
  State,
  PayloadAction<{ itemId: string; layers: Layer[] }>
> = (state, { payload: { itemId, layers } }) => {
  const item = state.allWorkspaceItems[itemId];
  item.map.layers = layers;
  item.isProcessed = false;
};

export const revertLayersToOriginal: CaseReducer<
  State,
  PayloadAction<string>
> = (state, { payload: itemId }) => {
  const item = state.allWorkspaceItems[itemId];
  assert(item.map.originalLayers, 'No original layers to revert to');
  item.map.layers = item.map.originalLayers;
  item.isProcessed = false;
};

export const recolorPNSymbolLayers: CaseReducer<
  State,
  PayloadAction<string>
> = (state, { payload: color }) => {
  const symbolItems = Object.values(state.allWorkspaceItems).filter((item) =>
    symbolItemIds.includes(item.id)
  );

  symbolItems.forEach((item) => {
    assert(item.map.originalLayers, 'Original layers are required to recolor');

    if (isEqual(item.map.layers, item.map.originalLayers)) {
      item.map.layers.forEach((layer) => {
        layer.style.color = color;
      });
    }

    item.map.originalLayers.forEach((layer) => {
      layer.style.color = color;
    });

    item.isProcessed = false;
  });
};

export const enableBubbleMapping: CaseReducer<
  State,
  PayloadAction<{ itemId: string; radius: unknown[]; color?: unknown }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  _enableBubbleMapping(item, {
    radius: payload.radius,
    color: payload.color,
  });
  item.isProcessed = false;
};

export const disableBubbleMapping: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  const item = state.allWorkspaceItems[payload];
  _disableBubbleMapping(item);
  item.isProcessed = false;
};

export const updateNumberOfElements: CaseReducer<
  State,
  PayloadAction<{ itemId: string; numberOfElements: number }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) item.numberOfElements = payload.numberOfElements;
};

export const updateName: CaseReducer<
  State,
  PayloadAction<{ itemId: string; name: string }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.name = payload.name;
};

export const addToWorkspace: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  state.idsInWorkspace = uniq([...state.idsInWorkspace, payload]);
  const item = state.allWorkspaceItems[payload];
  if (!isNil(item)) item.isVisible = true;
};

export const removeFromWorkspace: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  state.idsInWorkspace = state.idsInWorkspace.filter((id) => id !== payload);
  const item = state.allWorkspaceItems[payload];
  if (!isNil(item)) item.isProcessed = false;
};

export const setWorkspace: CaseReducer<
  State,
  PayloadAction<{ itemIds: string[]; preserveVisibility?: boolean }>
> = (state, { payload }) => {
  state.idsInWorkspace = prependModuleItemIds(payload.itemIds);

  if (!payload.preserveVisibility) {
    Object.values(state.allWorkspaceItems)
      .filter((item) => payload.itemIds.includes(item.id))
      .forEach((item) => {
        item.isVisible = true;
      });
  }
};

export const select: CaseReducer<State, PayloadAction<string | undefined>> = (
  state,
  { payload }
) => {
  state.workspaceItemIdSelected = payload;
};

export const updateCounts: CaseReducer<
  State,
  PayloadAction<{ itemId: string; totalCount: number; streamedCount: number }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) {
    item.totalCount = payload.totalCount;
    item.streamedCount = payload.streamedCount;
  }
};

export const updateVisibility: CaseReducer<
  State,
  PayloadAction<{ itemId: string; isVisible: boolean }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) item.isVisible = payload.isVisible;
};

export const updateTableDataItems: CaseReducer<
  State,
  PayloadAction<{ itemId: string; tableDataItems: DataItem[] }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) item.tableDataItems = payload.tableDataItems;
};

export const markAsLoaded: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  const item = state.allWorkspaceItems[payload];
  if (!isNil(item)) item.isLoaded = true;
};

export const markAsProcessed: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  const item = state.allWorkspaceItems[payload];
  if (!isNil(item)) item.isProcessed = true;
};

export const markAsUnfinalized: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  const item = state.allWorkspaceItems[payload];
  if (!isNil(item)) item.isRendered = false;
};

export const markAsRendered: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  const item = state.allWorkspaceItems[payload];
  if (!isNil(item)) item.isRendered = true;
};

export const markAsError: CaseReducer<
  State,
  PayloadAction<{ itemId: string; errorMessage: string | undefined }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) {
    item.isError = true;
    item.errorMessage = payload.errorMessage;
  }
};

export const revisualize: CaseReducer<State, PayloadAction<string>> = (
  state,
  { payload }
) => {
  const item = state.allWorkspaceItems[payload];
  if (!isNil(item)) {
    item.isProcessed = false;
    item.isRendered = false;
    item.isError = false;
    item.errorMessage = undefined;
    item.tableDataItems = [];
  }
};

export const resetInternalState: CaseReducer<State> = (state) => {
  Object.values(state.allWorkspaceItems).forEach((item) => {
    item.isVisible = true;
    item.isProcessed = false;
    item.isRendered = false;
    item.totalCount = 0;
    item.streamedCount = 0;
    item.tableDataItems = [];

    if (isSelectionItemId(item.id)) {
      item.query.scopeIds = [];
    }
  });
};

export const updateSorts: CaseReducer<
  State,
  PayloadAction<{ itemId: string; sorts: Sort[] }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.query.sorts = payload.sorts;
  item.isProcessed = false;
};

export const updateFiltering: CaseReducer<
  State,
  PayloadAction<{
    itemId: string;
    filters: Filter[];
    linkOperator: LinkOperator;
  }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.query.filters = payload.filters;
  item.query.filtersLinkOperator = payload.linkOperator;
  if (areFiltersApplied(payload.filters)) {
    item.query.page = 0;
  }
  item.isProcessed = false;
};

export const updatePage: CaseReducer<
  State,
  PayloadAction<{ itemId: string; page: number }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) {
    item.query.page = payload.page;
    item.isProcessed = false;
  }
};

export const recalculateAllPages: CaseReducer<
  State,
  PayloadAction<{
    prevPageSize: number;
    pageSize: number;
    id: DataItemId | undefined;
  }>
> = (state, { payload: { prevPageSize, pageSize, id } }) => {
  Object.values(state.allWorkspaceItems).forEach((item) => {
    const indexOnPage = item.tableDataItems.findIndex(
      (dataItem) => dataItem._id === id
    );

    if (indexOnPage === -1) {
      item.query.page = 0;
      item.isProcessed = false;
    } else {
      const currentIdIndex = item.query.page * prevPageSize + indexOnPage;
      item.query.page = Math.floor(currentIdIndex / pageSize);
      item.isProcessed = false;
    }
  });
};

export const setCheckedIds: CaseReducer<
  State,
  PayloadAction<{ itemId: string; ids: DataItemId[] }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  item.query.checkedIds = payload.ids;
};

export const updateScopeIds: CaseReducer<
  State,
  PayloadAction<{
    itemId: string;
    ids: DataItemId[];
  }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) {
    item.query.scopeIds = payload.ids;
    item.isProcessed = false;
  }
};

export const updateClickedIds: CaseReducer<
  State,
  PayloadAction<{
    itemId: string;
    ids: DataItemId[];
  }>
> = (state, { payload }) => {
  const item = state.allWorkspaceItems[payload.itemId];
  if (!isNil(item)) {
    item.query.clickedIds = payload.ids;
    item.isProcessed = false;
  }
};

/**
 * Ensure `_background` and `grids` are always present.
 * If not specified, they are prepended at the beginning of the array.
 */
function prependModuleItemIds(
  itemIds: WorkspaceItem['id'][]
): WorkspaceItem['id'][] {
  const ids = [...itemIds];

  for (let i = moduleItemIds.length - 1; i >= 0; i--) {
    const id = moduleItemIds[i];
    if (!itemIds.includes(id)) {
      ids.unshift(id);
    }
  }

  return ids;
}
